/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { Download, ShareButton } from 'components/Download';
// import { parse } from 'date-fns';

export default function FrecuencyChart({ frecuencyData }) {
  const [frecuency, setFrecuency] = useState([]);
  const intl = useIntl();
  const chartRef = useRef(null);
  // const parsed = (date) => parse(date, 'yyyyMMddHHmm', new Date());

  useEffect(() => {
    if (frecuencyData.length > 0) {
      const newData = frecuencyData.map((element) => {
        const date = new Date(element?.timestamp);
        return {
          hz: parseFloat(element?.mediciones?.frecuencia),
          datetime: date,
        };
      });

      newData.sort((a, b) => a.datetime - b.datetime);
      setFrecuency(newData);
    } else {
      setFrecuency([]);
    }
  }, [frecuencyData]);

  const frequencyValues = frecuency.map((f) => f.hz);

  // Evita error si frequencyValues está vacío
  const minFreq = frequencyValues.length ? Math.min(...frequencyValues) - 5 : 0;
  const maxFreq = frequencyValues.length
    ? Math.max(...frequencyValues) + 5
    : 100;

  const options = {
    chart: {
      type: 'line',
      height: 450,
      zoomType: 'x',
    },
    title: {
      text: 'Frecuencia de la Señal',
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Tiempo',
      },
      labels: {
        format: '{value:%H:%M}',
      },
    },
    yAxis: {
      title: {
        text: 'Frecuencia (Hz)',
      },
      min: minFreq,
      max: maxFreq,
    },
    tooltip: {
      shared: true,
      xDateFormat: '%A, %b %e, %H:%M:%S UTC',
      formatter: function () {
        const date = new Date(this.x);
        const locale = intl.formatMessage({ id: 'app.locale' });
        const options = {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
          timeZone: 'UTC',
        };

        let formattedDate = date
          .toLocaleString(locale, options)
          .replace(',', '');
        let s = `<b>${formattedDate}</b><br/>`;
        this.points.forEach((point) => {
          s += `<span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y, 2)}</b><br/>`;
        });
        return s;
      },
      pointFormat:
        '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f} Hz</b><br/>',
    },
    global: {
      useUTC: true,
    },
    time: {
      useUTC: true,
    },
    series: [
      {
        name: 'Frecuencia de la Señal (Hz)',
        data: frecuency.map((f) => [f.datetime.getTime(), f.hz]),
        color: '#3b82f6',
        lineWidth: 2,
        marker: {
          radius: 2,
          fillColor: '#2563eb',
        },
        fillOpacity: 0.2,
      },
    ],
    credits: { enabled: false },
    legend: {
      enabled: true,
    },
  };

  const min =
    frequencyValues.length > 0 ? Math.min(...frequencyValues).toFixed(1) : 0;
  const max =
    frequencyValues.length > 0 ? Math.max(...frequencyValues).toFixed(1) : 0;
  const avg =
    frequencyValues.length > 0
      ? (
          frequencyValues.reduce((a, b) => a + b, 0) / frequencyValues.length
        ).toFixed(1)
      : 0;

  return (
    <Box width="100%" ref={chartRef}>
      <Portlet>
        <PortletHeader noDivider>
          <PortletLabel title="Frecuencia de la Señal" />
          <PortletToolbar>
            <Box display="flex" gap={1}>
              <Download name="Frecuencia de la Señal" itemRef={chartRef} />
              <ShareButton itemRef={chartRef} name="Frecuencia de la Señal" />
            </Box>
          </PortletToolbar>
        </PortletHeader>
        <PortletContent>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </PortletContent>
      </Portlet>

      <Box width="100%" mt={3}>
        <Box
          display="flex"
          justifyContent="center"
          width="auto"
          flexDirection="column"
          alignItems="center"
          gap={3}
        >
          <Chip
            label="Frecuencia de la Señal"
            sx={{
              fontWeight: 700,
              backgroundColor: '#730065',
              fontSize: '16px',
              color: 'white',
            }}
          />
          <Table sx={{ maxWidth: 650, boxShadow: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{''}</TableCell>
                <TableCell align="center">Mínimo</TableCell>
                <TableCell align="center">Máximo</TableCell>
                <TableCell align="center">Promedio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">Frecuencia (Hz)</TableCell>
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: '#8CBA42',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  {min}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: '#D64539',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  {max}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: '#EAAB36',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  {avg}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
